'use client';
import _extends from '@babel/runtime/helpers/extends';
import React__default, { createContext, useState, useContext, forwardRef } from 'react';
import classnames from 'classnames';
import { useUniqueID } from '../../hooks/useUniqueID.js';
import { Arrow } from '../Icons/Arrow.js';
import { Heading } from '../Heading/Heading.js';

const AccordionContext = /*#__PURE__*/createContext({
  expandedItems: [],
  setExpandedItems: () => null,
  allowMultiple: false
});
AccordionContext.displayName = 'AccordionContext';
const Accordion = _ref => {
  let {
    children,
    allowMultiple = false,
    initialExpanded = [],
    className = '',
    icons,
    headingProps = {},
    ...rest
  } = _ref;
  const [expandedItems, setExpandedItems] = useState(initialExpanded);
  return /*#__PURE__*/React__default.createElement(AccordionContext.Provider, {
    value: {
      expandedItems,
      setExpandedItems,
      allowMultiple,
      icons,
      headingProps
    }
  }, /*#__PURE__*/React__default.createElement("section", _extends({
    className: `b-accordion ${className}`,
    "data-testid": "accordion"
  }, rest), children));
};
Accordion.displayName = 'Accordion';
const AccordionItem = _ref2 => {
  let {
    itemKey,
    disabled = false,
    children,
    className = ''
  } = _ref2;
  const {
    expandedItems,
    setExpandedItems,
    allowMultiple
  } = useContext(AccordionContext);
  const uniqueID = useUniqueID();
  const expanded = expandedItems.includes(itemKey);
  const classNames = classnames(className, 'b-accordion__item', {
    'b-accordion__item--disabled': disabled
  });
  const handleExpand = () => {
    if (allowMultiple) {
      if (expanded) {
        setExpandedItems(prev => prev.filter(key => key !== itemKey));
      } else {
        setExpandedItems(prev => [...prev, itemKey]);
      }
    } else {
      setExpandedItems(() => expanded ? [] : [itemKey]);
    }
  };
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNames
  }, React__default.Children.map(children, child => {
    return /*#__PURE__*/React__default.cloneElement(child, {
      expanded,
      onExpand: handleExpand,
      disabled,
      id: `${itemKey}-${uniqueID}`
    });
  }));
};
const AccordionHeader = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
    expanded,
    onExpand,
    children,
    className = '',
    disabled,
    id,
    onClick,
    ...rest
  } = _ref3;
  const {
    icons,
    headingProps = {}
  } = useContext(AccordionContext);
  const {
    collapsed: CollapsedIcon,
    expanded: ExpandedIcon
  } = icons || {};
  const headerClassNames = classnames('b-accordion__header', className);
  const buttonClassNames = classnames('b-accordion__header-button', {
    'b-accordion__header-button--expanded': expanded
  });
  const iconClassNames = classnames('b-accordion__header-icon', {
    'b-accordion__header-icon--expanded': expanded
  });
  const Icon = CollapsedIcon && ExpandedIcon ? expanded ? ExpandedIcon : CollapsedIcon : Arrow;
  return /*#__PURE__*/React__default.createElement("header", _extends({
    className: headerClassNames,
    "data-testid": "accordion-header"
  }, rest), /*#__PURE__*/React__default.createElement("button", {
    ref: ref,
    "aria-expanded": expanded,
    "aria-controls": id,
    onClick: event => {
      if (onExpand) onExpand();
      if (onClick) onClick(event);
    },
    className: buttonClassNames,
    disabled: disabled
  }, /*#__PURE__*/React__default.isValidElement(children) ? children : /*#__PURE__*/React__default.createElement(Heading, _extends({
    variant: "heading-sm",
    type: "h3",
    modifier: 600
  }, headingProps), children), /*#__PURE__*/React__default.createElement("span", {
    className: iconClassNames
  }, typeof Icon === 'function' ? /*#__PURE__*/React__default.createElement(Icon, null) : Icon)));
});
AccordionHeader.displayName = 'AccordionHeader';
const AccordionBody = _ref4 => {
  let {
    expanded,
    children,
    className = '',
    id,
    ...rest
  } = _ref4;
  const bodyClassNames = classnames('b-accordion__body', className, {
    'b-accordion__body--expanded': expanded
  });
  return /*#__PURE__*/React__default.createElement("div", _extends({
    className: bodyClassNames,
    "data-testid": "accordion-body",
    id: id
  }, rest), /*#__PURE__*/React__default.createElement("div", {
    className: "b-accordion__body-expandable"
  }, children));
};
AccordionBody.displayName = 'AccordionBody';

export { Accordion, AccordionBody, AccordionContext, AccordionHeader, AccordionItem };
